import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, OnInit, PLATFORM_ID, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { Events, AppEvent } from '../event';
import { getPublicAppUrl } from '../public-url';
import { exitPresentation, isPresentationFullscreen, requestPresentationFullscreen } from '../presentation';

@Component({
  selector: 'app-selfie-access',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  template: `
    <div #presentationRoot class="relative flex min-h-[100dvh] w-screen overflow-hidden bg-slate-950 text-white font-sans bg-cover bg-center"
         [style.fontFamily]="event()?.font || 'Inter'"
         [style.background-image]="backgroundImageStyle()"
         (mousemove)="onPresentationActivity()"
         (touchstart)="onPresentationActivity()">

      @if (event()?.kioskBackgroundUrl && isVideo(event()?.kioskBackgroundUrl)) {
        <video [src]="event()?.kioskBackgroundUrl" class="absolute inset-0 z-0 h-full w-full object-cover transform-gpu" autoplay muted loop playsinline preload="auto"></video>
      }

      <div class="absolute inset-0 z-0 bg-slate-950/35"></div>

      <main class="relative z-10 flex min-h-[100dvh] w-full flex-col items-center justify-center px-6 py-8 text-center">
        <section class="flex w-full max-w-5xl -translate-y-4 flex-col items-center gap-10 md:-translate-y-6">
          <div class="flex flex-col items-center gap-5" [style.transform]="'scale(' + (event()?.kioskLogoScale || 1) + ')'">
            @if (event()?.titleImageUrl) {
              <img [src]="event()?.titleImageUrl" class="max-h-28 max-w-[78vw] object-contain drop-shadow-2xl md:max-h-36" alt="Logo del evento" />
            } @else {
              <h1 class="max-w-4xl text-4xl font-black uppercase italic tracking-tight drop-shadow-2xl md:text-7xl">{{ event()?.title }}</h1>
            }
            @if (event()?.additionalImageUrl) {
              <img [src]="event()?.additionalImageUrl" class="max-h-16 max-w-[72vw] object-contain drop-shadow-xl md:max-h-20" alt="Marca adicional" />
            } @else if (event()?.titleImageUrl) {
              <p class="max-w-3xl text-xl font-black uppercase italic tracking-tight text-white drop-shadow-xl md:text-3xl">{{ event()?.title }}</p>
            }
          </div>

          <div class="relative mt-4">
            <div class="absolute -inset-3 rounded-[2.45rem] overflow-hidden shadow-[0_0_42px_rgba(255,255,255,0.2)] qr-scan-frame"></div>
            <div class="relative rounded-[2rem] bg-white p-5 shadow-[0_35px_90px_rgba(0,0,0,0.45)] md:p-7">
              <img [src]="qrCodeUrl(selfieUrl())" class="h-64 w-64 md:h-80 md:w-80" alt="QR de acceso a selfie" />
            </div>
          </div>

          <div class="space-y-3">
            <p class="text-[10px] font-black uppercase tracking-[0.35em] text-white/65">Escanea el QR</p>
            <h2 class="text-3xl font-black uppercase italic tracking-tight md:text-5xl">Compartí tu selfie</h2>
            <p class="mx-auto max-w-2xl text-2xl font-black leading-relaxed text-white md:text-4xl">
              Bienvenid&#64;!
            </p>
          </div>
        </section>
      </main>

      @if (presentationMode()) {
        <button type="button"
                (click)="togglePresentationFullscreen()"
                class="fixed right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/25 text-white shadow-xl backdrop-blur-md transition-opacity duration-300 hover:bg-black/45 active:scale-95"
                [ngClass]="showPresentationControls() ? 'opacity-100' : 'opacity-0 pointer-events-none'">
          <mat-icon class="text-[20px]">{{ isPresentationFullscreen() ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
        </button>
      }
    </div>
  `
  ,
  styles: [`
    .qr-scan-frame::before {
      content: '';
      position: absolute;
      inset: -45%;
      background: linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.85) 49%, rgba(255,255,255,0.28) 53%, transparent 64%);
      transform: translateX(-55%) rotate(0deg);
      animation: qrFrameSweep 4.8s ease-in-out infinite;
    }

    .qr-scan-frame::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      box-shadow: inset 0 0 24px rgba(255,255,255,0.12);
    }

    @keyframes qrFrameSweep {
      0%, 18% { transform: translateX(-65%) rotate(0deg); opacity: 0; }
      32% { opacity: 0.9; }
      56% { transform: translateX(65%) rotate(0deg); opacity: 0.75; }
      78%, 100% { transform: translateX(75%) rotate(0deg); opacity: 0; }
    }
  `]
})
export class SelfieAccess implements OnInit {
  private route = inject(ActivatedRoute);
  private events = inject(Events);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  @ViewChild('presentationRoot') presentationRoot?: ElementRef<HTMLElement>;

  event = signal<AppEvent | null>(null);
  eventId = signal('');
  presentationMode = signal(false);
  isPresentationFullscreen = signal(false);
  showPresentationControls = signal(false);
  private presentationControlsTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly appUrl = getPublicAppUrl();

  ngOnInit() {
    this.presentationMode.set(this.route.snapshot.queryParamMap.get('presentation') === '1');
    this.route.params.subscribe(params => {
      const identifier = params['id'];
      this.events.getEvent(identifier).subscribe(evt => {
        this.eventId.set(evt.slug || evt.id);
        this.events.joinEvent(evt.id);
        this.event.set(evt);
        if (this.presentationMode()) {
          setTimeout(() => requestPresentationFullscreen(this.presentationRoot?.nativeElement), 250);
          this.onPresentationActivity();
        }
      });
      this.events.eventUpdated$.subscribe(updatedEvent => {
        if (updatedEvent.id !== this.event()?.id) return;
        this.event.set({ ...this.event(), ...updatedEvent } as AppEvent);
      });
    });
  }

  selfieUrl() {
    return `${this.appUrl}/e/${this.eventId()}/selfie`;
  }

  qrCodeUrl(data: string) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=700x700&margin=12&color=000000&data=${encodeURIComponent(data)}`;
  }

  backgroundImageStyle() {
    const url = this.event()?.kioskBackgroundUrl;
    return url && !this.isVideo(url) ? `url(${url})` : 'radial-gradient(circle at center, #1e293b 0%, #020617 70%)';
  }

  isVideo(url: string | undefined | null) {
    return !!url && /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
  }

  safeYoutubeUrl(): SafeResourceUrl | null {
    const videoId = this.event()?.kioskYoutubeVideoId;
    if (!videoId) return null;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&enablejsapi=1&disablekb=1&fs=0&playsinline=1&origin=${encodeURIComponent(origin)}`);
  }

  onPresentationActivity() {
    if (!this.presentationMode()) return;
    this.showPresentationControls.set(true);
    if (this.presentationControlsTimeout) clearTimeout(this.presentationControlsTimeout);
    this.presentationControlsTimeout = setTimeout(() => {
      this.showPresentationControls.set(false);
    }, 2200);
  }

  async togglePresentationFullscreen() {
    if (isPresentationFullscreen()) {
      exitPresentation();
      this.isPresentationFullscreen.set(false);
      return;
    }
    await requestPresentationFullscreen(this.presentationRoot?.nativeElement);
    this.isPresentationFullscreen.set(isPresentationFullscreen());
  }
}
